import Point from 'ol/geom/Point';
import LineString from 'ol/geom/LineString';
import { apply as applyTransform, create as createTransform, } from 'ol/transform';
import { toRgbaNormalized } from '../util/color.js';
/**
 * A {@link LabelSink} recording each drawn label as a {@link LabelPlacement} in
 * screen CSS px for a WebGL layer to compose as textured quads, plus edge-label
 * leaders as flat screen-px segments.
 */
export class PlacementSink {
    toPixel_ = createTransform();
    scratch_ = [0, 0];
    placements = [];
    count = 0;
    /** Flat [x0, y0, x1, y1, …] leader segments in screen CSS px. */
    leaders = [];
    /** Style shared by this frame's leaders (they all use one tick style). */
    leaderStroke = null;
    stroke_ = null;
    begin(toPixel) {
        this.toPixel_ = toPixel;
        this.count = 0;
        this.leaders.length = 0;
        this.leaderStroke = null;
        this.stroke_ = null;
    }
    setStyle(style) {
        const stroke = style.getStroke();
        if (!stroke) {
            this.stroke_ = null;
            return;
        }
        const [r, g, b, a] = toRgbaNormalized(stroke.getColor());
        const dash = stroke.getLineDash();
        const period = dash && dash.length ? dash.reduce((s, v) => s + v, 0) : 0;
        this.stroke_ = {
            r,
            g,
            b,
            a,
            width: stroke.getWidth() ?? 1,
            dashOn: dash && dash.length ? (dash[0] ?? 0) : 0,
            dashPeriod: period,
            dashOffset: stroke.getLineDashOffset() ?? 0,
        };
        this.leaderStroke = this.stroke_;
    }
    drawGeometry(geometry) {
        if (!(geometry instanceof LineString) || !this.stroke_)
            return;
        const flat = geometry.getFlatCoordinates();
        const stride = geometry.getStride();
        const s = this.scratch_;
        for (let i = 0; i + stride + 1 < flat.length; i += stride) {
            s[0] = flat[i] ?? 0;
            s[1] = flat[i + 1] ?? 0;
            applyTransform(this.toPixel_, s);
            const ax = s[0];
            const ay = s[1];
            s[0] = flat[i + stride] ?? 0;
            s[1] = flat[i + stride + 1] ?? 0;
            applyTransform(this.toPixel_, s);
            this.leaders.push(ax, ay, s[0], s[1]);
        }
    }
    drawFeature(feature, style) {
        const geom = feature.getGeometry();
        if (!(geom instanceof Point))
            return;
        const text = style.getText();
        if (!text)
            return;
        const content = textToString(text.getText());
        if (!content)
            return;
        const flat = geom.getFlatCoordinates();
        const s = this.scratch_;
        s[0] = flat[0] ?? 0;
        s[1] = flat[1] ?? 0;
        applyTransform(this.toPixel_, s);
        const p = this.slot_();
        p.x = s[0];
        p.y = s[1];
        p.text = content;
        fillTextStyle(p, text);
    }
    slot_() {
        let p = this.placements[this.count];
        if (!p) {
            p = blankPlacement();
            this.placements.push(p);
        }
        this.count++;
        return p;
    }
}
function fillTextStyle(p, text) {
    // Fill alpha becomes per-quad opacity; halo alpha is stored relative to it.
    const [fr, fg, fb, fa] = toRgbaNormalized(text.getFill()?.getColor());
    p.opacity = fa;
    p.fill[0] = fr;
    p.fill[1] = fg;
    p.fill[2] = fb;
    const stroke = text.getStroke();
    if (stroke) {
        const [hr, hg, hb, ha] = toRgbaNormalized(stroke.getColor());
        p.halo[0] = hr;
        p.halo[1] = hg;
        p.halo[2] = hb;
        p.haloAlpha = fa > 0 ? Math.min(1, ha / fa) : ha;
        p.haloWidth = stroke.getWidth() ?? 0;
    }
    else {
        p.haloAlpha = 0;
        p.haloWidth = 0;
    }
    p.font = text.getFont() ?? '';
    p.align = text.getTextAlign() ?? 'center';
    p.baseline = text.getTextBaseline() ?? 'middle';
    p.offsetX = text.getOffsetX();
    p.offsetY = text.getOffsetY();
    p.rotation = text.getRotation() ?? 0;
}
function textToString(text) {
    if (typeof text === 'string')
        return text;
    if (Array.isArray(text))
        return text.filter((_, i) => i % 2 === 0).join('');
    return '';
}
function blankPlacement() {
    return {
        x: 0,
        y: 0,
        text: '',
        font: '',
        fill: [0, 0, 0],
        halo: [0, 0, 0],
        haloAlpha: 0,
        haloWidth: 0,
        opacity: 1,
        align: 'center',
        baseline: 'middle',
        offsetX: 0,
        offsetY: 0,
        rotation: 0,
    };
}
//# sourceMappingURL=PlacementSink.js.map